import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLogs, useLog, useConversation } from '@/api/use-logs';
import { useItemsByIds } from '@/api/use-items';
import type { TrainingLog } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pagination } from '@/components/shared/Pagination';
import { ConversationReplay } from '@/components/logs/ConversationReplay';
import { SessionInsights } from '@/components/logs/SessionInsights';
import { LOG_TYPE_COLORS } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { aggregateMetadata } from '@/lib/metadata-utils';
import {
  Clock,
  ChevronDown,
  ChevronRight,
  X,
  Sparkles,
  CheckCircle2,
  XCircle,
  Calendar,
  Filter,
  MessageCircle,
  BarChart3,
} from 'lucide-react';

// ---------- constants ----------

const LOG_TYPES = [
  { value: '', label: 'All' },
  { value: 'training', label: 'Training' },
  { value: 'review', label: 'Review' },
];

const DATE_RANGES = [
  { value: '7', label: 'Last 7 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 90 Days' },
  { value: 'all', label: 'All Time' },
];

const QUALITY_LABELS: Record<number, string> = {
  5: 'Perfect',
  4: 'Good',
  3: 'OK',
  2: 'Again',
  1: 'Hard',
  0: 'Fail',
};

const SCORE_DIMENSIONS = [
  { key: 'score_fluency' as const, label: 'FLUENCY' },
  { key: 'score_vocab' as const, label: 'VOCAB' },
  { key: 'score_naturalness' as const, label: 'NATURALNESS' },
  { key: 'score_interaction' as const, label: 'INTERACTION' },
  { key: 'score_progression' as const, label: 'PROGRESSION' },
  { key: 'score_repair' as const, label: 'REPAIR' },
];

// ---------- helpers ----------

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatFullDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function groupByDate(logs: TrainingLog[]) {
  const groups: Record<string, TrainingLog[]> = {};
  for (const log of logs) {
    const dateKey = log.date.slice(0, 10);
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(log);
  }
  return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
}

function parseStuckPoints(raw: string | null): string[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.map(p => typeof p === 'object' && p?.text ? p.text : String(p));
  } catch {
    // not JSON
  }
  return [raw];
}

function scoreColor(pct: number): string {
  if (pct >= 70) return 'text-blue-600 bg-blue-50 border-blue-200';
  if (pct >= 50) return 'text-amber-600 bg-amber-50 border-amber-200';
  return 'text-red-600 bg-red-50 border-red-200';
}

// ---------- sub-components ----------

function ReviewStats({ logId }: { logId: string }) {
  const { data: log } = useLog(logId);
  if (!log?.item_results?.length) return null;

  const total = log.item_results.length;
  const pass = log.item_results.filter(r => (r.quality ?? 0) >= 3).length;
  const fail = total - pass;

  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="text-muted-foreground flex items-center gap-1">
        <Filter className="w-3.5 h-3.5" />
        {total} items
      </span>
      <span className="text-green-600 flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
        {pass} Pass
      </span>
      <span className="text-red-500 flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
        {fail} Fail
      </span>
    </div>
  );
}

function ReviewRetention({ logId }: { logId: string }) {
  const { data: log } = useLog(logId);
  if (!log?.item_results?.length) return null;

  const total = log.item_results.length;
  const pass = log.item_results.filter(r => (r.quality ?? 0) >= 3).length;
  const retention = Math.round((pass / total) * 100);

  return (
    <Badge className="bg-green-100 text-green-700 border-0 text-xs font-semibold">
      {retention}% RETENTION
    </Badge>
  );
}

// ---------- Session Analysis Panel ----------

function SessionAnalysis({ logId, onClose }: { logId: string; onClose: () => void }) {
  const { data: log, isLoading } = useLog(logId);
  const { data: conversation } = useConversation(logId);
  const [activeTab, setActiveTab] = useState<'analysis' | 'conversation' | 'insights'>('analysis');

  // Collect all unique item IDs from conversation turns, metadata, and item_results for batch fetch
  const conversationItemIds = useMemo(() => {
    const ids = new Set<string>();
    // From conversation turn item links
    if (conversation) {
      for (const turn of conversation) {
        if (turn.items) {
          for (const link of turn.items) ids.add(link.item_id);
        }
        // From metadata: session_target_items + target_items_hit
        if (turn.metadata) {
          try {
            const meta = JSON.parse(turn.metadata);
            if (Array.isArray(meta.session_target_items)) meta.session_target_items.forEach((id: string) => ids.add(id));
            if (Array.isArray(meta.target_items_hit)) meta.target_items_hit.forEach((id: string) => ids.add(id));
          } catch { /* ignore */ }
        }
      }
    }
    // From log item_results
    if (log?.item_results) {
      for (const ir of log.item_results) ids.add(ir.item_id);
    }
    return [...ids];
  }, [conversation, log]);

  const { data: itemsMap } = useItemsByIds(conversationItemIds);

  if (isLoading) {
    return (
      <div className="w-full lg:w-[40%] border-l bg-background p-6 space-y-4 overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Session Analysis</h2>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-20 bg-muted rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (!log) return null;

  const stuckPoints = parseStuckPoints(log.stuck_points);

  return (
    <div className="w-full lg:w-[40%] border-l bg-background overflow-y-auto">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Session Analysis</h2>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Tab toggle: Analysis / Conversation */}
        <div className="flex gap-1 bg-muted rounded-lg p-1">
          <button
            onClick={() => setActiveTab('analysis')}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
              activeTab === 'analysis'
                ? 'bg-white text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <BarChart3 className="w-3.5 h-3.5" />
            Analysis
          </button>
          <button
            onClick={() => setActiveTab('conversation')}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
              activeTab === 'conversation'
                ? 'bg-white text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <MessageCircle className="w-3.5 h-3.5" />
            Conversation
          </button>
          <button
            onClick={() => setActiveTab('insights')}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
              activeTab === 'insights'
                ? 'bg-white text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <BarChart3 className="w-3.5 h-3.5" />
            Insights
          </button>
        </div>

        {/* Conversation Tab */}
        {activeTab === 'conversation' && (
          <ConversationReplay turns={conversation ?? []} itemsMap={itemsMap ?? new Map()} />
        )}

        {/* Insights Tab */}
        {activeTab === 'insights' && conversation && (
          <SessionInsights turns={conversation} cardDifficulty={log?.card_difficulty ?? 3} itemsMap={itemsMap ?? undefined} />
        )}

        {/* Analysis Tab Content */}
        {activeTab === 'analysis' && (<>

        {/* Score Grid 2x2 */}
        {SCORE_DIMENSIONS.some(d => log[d.key] != null) && (
          <div className="grid grid-cols-2 gap-3">
            {SCORE_DIMENSIONS.map(d => {
              const raw = log[d.key];
              if (raw == null) return null;
              // Scores 1-10 → multiply by 10 for percentage; already 0-100 → use as-is
              const pct = raw <= 10 ? raw * 10 : raw;
              return (
                <div
                  key={d.key}
                  className={cn(
                    'rounded-lg border p-4 text-center space-y-1',
                    scoreColor(pct),
                  )}
                >
                  <div className="text-xs font-semibold tracking-wider uppercase opacity-70">
                    {d.label}
                  </div>
                  <div className="text-2xl font-bold">{pct}%</div>
                </div>
              );
            })}
          </div>
        )}

        {/* AI Learning Insight */}
        {log.ai_summary && (
          <div>
            <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase mb-2">
              <Sparkles className="w-3.5 h-3.5" />
              AI Learning Insight
            </div>
            <blockquote className="border-l-2 border-primary/30 pl-3 text-sm text-muted-foreground italic">
              {log.ai_summary}
            </blockquote>
          </div>
        )}

        {/* Stuck Points */}
        {stuckPoints && stuckPoints.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">
              Stuck Points
            </div>
            <div className="space-y-2">
              {stuckPoints.map((point, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <Badge
                    variant="secondary"
                    className="bg-red-100 text-red-700 border-0 text-[10px] font-bold shrink-0 mt-0.5"
                  >
                    LVL {i + 1}
                  </Badge>
                  <span>{point}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Item History */}
        {log.item_results && log.item_results.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">
              Item History
            </div>
            <div className="space-y-2">
              {log.item_results.map((item, i) => {
                const q = item.quality ?? 0;
                const passed = q >= 3;
                const xp = passed ? `+${q * 4}xp` : `-${(3 - q) * 2}xp`;
                const itemData = itemsMap?.get(item.item_id);
                const itemText = itemData?.text ?? item.item_id.slice(0, 12);
                const resultColors: Record<string, string> = {
                  pass: 'bg-green-100 text-green-700',
                  fail: 'bg-red-100 text-red-700',
                  first_time: 'bg-blue-100 text-blue-700',
                };
                const resultLabels: Record<string, string> = {
                  pass: 'Pass',
                  fail: 'Fail',
                  first_time: 'New',
                };
                return (
                  <div
                    key={i}
                    className="flex items-center gap-3 rounded-lg border px-3 py-2"
                  >
                    {passed ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-2">
                        {itemText}
                        {item.result && (
                          <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full font-medium', resultColors[item.result] ?? 'bg-gray-100 text-gray-600')}>
                            {resultLabels[item.result] ?? item.result}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {QUALITY_LABELS[q] ?? `Q${q}`}
                        {item.context ? ` \u00b7 ${item.context}` : ''}
                      </div>
                    </div>
                    <span
                      className={cn(
                        'text-xs font-semibold shrink-0',
                        passed ? 'text-green-600' : 'text-red-500',
                      )}
                    >
                      {xp}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* New Phrases */}
        {log.new_phrases && (
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">
              New Phrases
            </div>
            <div className="flex flex-wrap gap-2">
              {(() => {
                try {
                  const phrases = JSON.parse(log.new_phrases as string);
                  if (Array.isArray(phrases)) {
                    return phrases.map((p: string, i: number) => (
                      <span key={i} className="text-sm bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-md font-medium">{p}</span>
                    ));
                  }
                } catch { /* not JSON */ }
                return <span className="text-sm text-muted-foreground">{log.new_phrases}</span>;
              })()}
            </div>
          </div>
        )}

        {/* Training Dynamics — only for training logs with metadata */}
        {conversation && (() => {
          const agg = aggregateMetadata(conversation);
          if (!agg.hasMetadata) return null;
          return (
            <div className="mt-6 border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Training Dynamics</h4>

              {/* Target vocab hit rate */}
              {agg.targetItems.length > 0 && (
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Target Vocabulary</span>
                    <span>{agg.hitCount}/{agg.targetItems.length} ({Math.round(agg.hitRate * 100)}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${agg.hitRate * 100}%` }} />
                  </div>
                </div>
              )}

              {/* New words introduced */}
              {agg.newWordsIntroduced.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">New Words Introduced ({agg.newWordsUnderstoodCount} understood)</p>
                  <div className="flex flex-wrap gap-1">
                    {agg.newWordsIntroduced.map(w => (
                      <span key={w} className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded">{w}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Difficulty timeline */}
              {agg.difficultyEvents.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">Difficulty Changes</p>
                  {agg.difficultyEvents.map((e, i) => (
                    <p key={i} className="text-xs text-gray-600">
                      Turn {e.turn}: {e.direction === 'up' ? '↑ Up' : '↓ Down'}
                    </p>
                  ))}
                </div>
              )}

              {/* Comprehension trend dots */}
              {agg.comprehensionLevels.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">Comprehension Trend</p>
                  <div className="flex gap-1">
                    {agg.comprehensionLevels.map((c, i) => {
                      const colors = { high: 'bg-green-500', medium: 'bg-yellow-500', low: 'bg-red-500' };
                      return <span key={i} className={`w-3 h-3 rounded-full ${colors[c.level]}`} title={`Turn ${c.turn}: ${c.level}`} />;
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        </>)}
      </div>
    </div>
  );
}

// ---------- Session Card ----------

function TrainingCard({
  log,
  isSelected,
  onSelect,
}: {
  log: TrainingLog;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const normalizedScore = log.score != null ? (log.score <= 1 ? log.score * 10 : log.score) : null;
  const progressPct = normalizedScore != null ? Math.round((normalizedScore / 10) * 100) : null;

  return (
    <button
      onClick={onSelect}
      className={cn(
        'w-full text-left rounded-lg border p-4 transition-all hover:shadow-sm',
        isSelected
          ? 'border-primary/40 bg-primary/5 shadow-sm'
          : 'border-border bg-card hover:bg-muted/30',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0 space-y-2">
          {/* Top row: badge + time */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant="secondary"
              className={cn('text-[10px] font-bold uppercase tracking-wider border-0', LOG_TYPE_COLORS[log.log_type])}
            >
              {log.log_type}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {formatTime(log.created_at)}
            </span>
          </div>

          {/* Title */}
          <div className="font-medium text-sm">
            {log.title ?? (log.log_type === 'training' ? 'Training Session' : 'Vocabulary Retention')}
          </div>

          {/* Meta row */}
          <div className="flex items-center gap-3 flex-wrap">
            {log.duration_min != null && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {log.duration_min < 60
                  ? `${String(log.duration_min).padStart(2, '0')} min`
                  : `${Math.floor(log.duration_min / 60)}h ${log.duration_min % 60}m`}
              </span>
            )}

            {log.log_type === 'training' && log.stuck_points && (
              <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                {(() => {
                  const pts = parseStuckPoints(log.stuck_points);
                  return pts?.[0] ? `${pts[0]} Focus` : null;
                })()}
              </span>
            )}

            {log.log_type === 'review' && <ReviewStats logId={log.id} />}
          </div>
        </div>

        {/* Right side: progress/retention badge + score + chevron */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex flex-col items-end gap-1.5">
            {log.log_type === 'training' && progressPct != null && (
              <Badge className="bg-green-100 text-green-700 border-0 text-xs font-semibold">
                {progressPct}% PROGRESS
              </Badge>
            )}
            {log.log_type === 'review' && <ReviewRetention logId={log.id} />}
            {normalizedScore != null && (
              <span className="text-sm font-semibold text-foreground">
                {normalizedScore.toFixed(1)}/10
              </span>
            )}
          </div>
          <ChevronRight
            className={cn(
              'w-4 h-4 text-muted-foreground transition-transform',
              isSelected && 'rotate-180',
            )}
          />
        </div>
      </div>
    </button>
  );
}

// ---------- Collapsed Day ----------

function CollapsedDay({
  date,
  count,
  onExpand,
}: {
  date: string;
  count: number;
  onExpand: () => void;
}) {
  return (
    <button
      onClick={onExpand}
      className="w-full text-left rounded-lg border border-dashed border-muted-foreground/30 px-4 py-3 text-sm text-muted-foreground hover:bg-muted/30 transition-colors"
    >
      <span className="font-medium">{formatFullDate(date)}</span>
      <span className="mx-2">&middot;</span>
      <span>{count} session{count !== 1 ? 's' : ''} recorded this day</span>
      <span className="mx-2">&middot;</span>
      <span className="text-primary">Click to expand</span>
    </button>
  );
}

// ---------- Main page ----------

export default function Logs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [logType, setLogType] = useState('');
  const [offset, setOffset] = useState(0);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(searchParams.get('id'));
  const [dateRange, setDateRange] = useState('30');

  // Sync URL param → state on mount
  useEffect(() => {
    const id = searchParams.get('id');
    if (id && id !== selectedLogId) setSelectedLogId(id);
    // Clear URL param after consuming
    if (id) {
      searchParams.delete('id');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [sceneFilter, setSceneFilter] = useState<string | null>(null);
  const [collapsedDates, setCollapsedDates] = useState<Set<string>>(new Set());
  const limit = 20;

  const { data, isLoading } = useLogs({
    log_type: logType || undefined,
    offset,
    limit,
  });

  const logs = data?.data ?? [];
  const pagination = data?.pagination;

  // Client-side date filtering
  const filteredLogs = useMemo(() => {
    if (dateRange === 'all') return logs;
    const days = parseInt(dateRange, 10);
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return logs.filter(log => new Date(log.date) >= cutoff);
  }, [logs, dateRange]);

  const grouped = groupByDate(filteredLogs);

  const toggleCollapse = (date: string) => {
    setCollapsedDates(prev => {
      const next = new Set(prev);
      next.has(date) ? next.delete(date) : next.add(date);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        {/* Left: type tabs */}
        <div className="flex gap-1">
          {LOG_TYPES.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => {
                setLogType(value);
                setOffset(0);
                setSelectedLogId(null);
              }}
              className={cn(
                'px-4 py-1.5 rounded-full text-sm font-medium transition-colors',
                logType === value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Right: date range + scene chip */}
        <div className="flex items-center gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-44 h-9">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-3.5 h-3.5" />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGES.map(r => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {sceneFilter && (
            <Badge
              variant="secondary"
              className="flex items-center gap-1 pl-2.5 pr-1.5 py-1 cursor-pointer"
              onClick={() => setSceneFilter(null)}
            >
              {sceneFilter}
              <X className="w-3 h-3" />
            </Badge>
          )}
        </div>
      </div>

      {/* Main content: timeline + analysis panel */}
      <div className="flex gap-0 min-h-[600px]">
        {/* Timeline (left) */}
        <div
          className={cn(
            'space-y-6 overflow-y-auto pr-0 transition-all',
            selectedLogId ? 'w-full lg:w-[60%] lg:pr-4' : 'w-full',
          )}
        >
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-20 bg-muted rounded-lg animate-pulse" />
              ))}
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              <Clock className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-lg font-medium">No training logs yet</p>
              <p className="text-sm mt-1">Start a training or review session to see logs here</p>
            </div>
          ) : (
            grouped.map(([date, dayLogs]) => {
              const isCollapsed = collapsedDates.has(date);

              if (isCollapsed) {
                return (
                  <CollapsedDay
                    key={date}
                    date={date}
                    count={dayLogs.length}
                    onExpand={() => toggleCollapse(date)}
                  />
                );
              }

              return (
                <div key={date}>
                  {/* Date header with timeline dot */}
                  <button
                    onClick={() => toggleCollapse(date)}
                    className="flex items-center gap-2 mb-3 group cursor-pointer"
                  >
                    <span className="w-2.5 h-2.5 rounded-full bg-primary shrink-0" />
                    <span className="text-sm font-semibold text-foreground">
                      {formatFullDate(date)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      ({dayLogs.length} session{dayLogs.length !== 1 ? 's' : ''})
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>

                  {/* Session cards */}
                  <div className="space-y-2 ml-1 pl-4 border-l-2 border-muted">
                    {dayLogs.map(log => (
                      <TrainingCard
                        key={log.id}
                        log={log}
                        isSelected={selectedLogId === log.id}
                        onSelect={() =>
                          setSelectedLogId(selectedLogId === log.id ? null : log.id)
                        }
                      />
                    ))}
                  </div>
                </div>
              );
            })
          )}

          {/* Pagination */}
          {pagination && pagination.total > limit && (
            <Pagination
              total={pagination.total}
              offset={offset}
              limit={limit}
              onOffsetChange={setOffset}
            />
          )}
        </div>

        {/* Right panel: Session Analysis */}
        {selectedLogId && (
          <SessionAnalysis
            logId={selectedLogId}
            onClose={() => setSelectedLogId(null)}
          />
        )}
      </div>
    </div>
  );
}
